import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { motion } from 'framer-motion';
import { heroSlides, brandInfo } from '../data/mock';
import { Play, ChevronDown } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const HeroSlider = () => {
  const scrollToAbout = () => {
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        slidesPerView={1}
        spaceBetween={0}
        loop={true}
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        effect="fade"
        fadeEffect={{
          crossFade: true,
        }}
        speed={1000}
        pagination={{
          el: '.hero-pagination',
          clickable: true,
          dynamicBullets: true,
        }}
        className="h-full w-full"
      >
        {heroSlides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <div 
              className="relative h-full w-full flex items-end"
              style={{ backgroundColor: slide.bgColor }}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              </div>

              {/* Content */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative z-10 w-full p-6 lg:p-12 pb-24 lg:pb-32"
              >
                <div className="max-w-7xl mx-auto">
                  {/* Tags */}
                  <div className="flex items-center gap-3 mb-4">
                    <span 
                      className="service-tag"
                      style={{ backgroundColor: slide.textColor, color: '#151515' }}
                    >
                      Deep House
                    </span>
                    <span 
                      className="service-tag"
                      style={{ backgroundColor: '#88a2ff', color: '#151515' }}
                    >
                      Soulful
                    </span>
                  </div>

                  {/* Title */}
                  <h1 
                    className="font-display text-5xl md:text-7xl lg:text-9xl font-extrabold uppercase leading-none mb-4"
                    style={{ color: slide.textColor }}
                  >
                    {slide.title}
                  </h1>

                  {/* Subtitle */}
                  <p className="text-white/80 text-xl md:text-2xl font-normal mb-2">
                    {slide.subtitle}
                  </p>
                  <p className="text-white/60 text-lg font-normal mb-8">
                    {slide.description}
                  </p>

                  {/* CTA Button */}
                  <button className="cta-button group">
                    <Play className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                    Listen Now
                  </button>
                </div>
              </motion.div>

              {/* Brand Text Overlay */}
              <div className="absolute bottom-4 right-4 lg:right-12 z-20">
                <p 
                  className="font-display text-6xl md:text-8xl lg:text-[10rem] font-extrabold uppercase opacity-10"
                  style={{ color: slide.textColor }}
                >
                  Loxton
                </p>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Pagination */}
      <div className="hero-pagination absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2" />

      {/* Scroll Indicator */}
      <motion.button
        onClick={scrollToAbout}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 text-white/60 hover:text-white transition-colors"
      >
        <ChevronDown className="w-8 h-8" />
      </motion.button>
    </section>
  );
};

export default HeroSlider;
